import React, { useEffect, useState } from "react"
import parse from "html-react-parser"
import { ArrowDownIcon, ArrowUpIcon, LoadingSpinner, UpDownArrowIcon } from "../Icons";
import { TableColumn } from "../../../models/dataTable.model";
import "./index.css"


function validateColumnWidths(columns: TableColumn[]): any | TableColumn[] {
  const totalWidth = columns.reduce((sum, col) => sum + parseInt(col.width, 10), 0);
  if (totalWidth > 100) {
    throw new Error(`Column widths exceed 100%. Total: ${totalWidth}%`);
  } else if (totalWidth < 100) {
    return columns.map(col => ({ ...col, width: col.width + Math.max(1, (100 - totalWidth) / columns.length) }))
  } else
    return columns
}


type TableProps = {
  columns: TableColumn[]
  rows: { [key: string]: any; detail?: React.ReactNode }[]
  loading: boolean
  onRowDoubleClick: (row: any) => void
  selectable?: boolean
  handleRowSelection?: (s: string[]) => void
  resetRowsSelection?: boolean
  onClickIdValue?: (id: any) => void
  hasDetail?: boolean
  columnId?: string
  detailColumn?: string
  hashtagPrefixId?: boolean
}

const DataTable: React.FC<TableProps> = ({
  columns,
  rows,
  loading,
  onRowDoubleClick,
  onClickIdValue,
  selectable = false,
  handleRowSelection,
  resetRowsSelection,
  hasDetail = false,
  columnId = 'id',
  detailColumn = 'description',
  hashtagPrefixId = false
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filterText, setFilterText] = useState<string>("");
  const pageSize = 15;

  const [isFading, setIsFading] = useState(true);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allRowsSelected, setAllRowsSelected] = useState<boolean>(false)

  useEffect(() => {
    setSelectedRows([])
    setAllRowsSelected(false)
    if (handleRowSelection) handleRowSelection([])
  }, [resetRowsSelection])

  useEffect((
  ) => {
    loading ? setIsFading(true) : setTimeout(() => { setIsFading(false) }, 200);
    setCurrentPage(1)
    setSelectedRows([])
    setAllRowsSelected(false)
    if (handleRowSelection) handleRowSelection([])
  }, [loading])

  const [hiddenDetails, setHiddenDetails] = useState<number[]>([])
  const visibleColumns: TableColumn[] = validateColumnWidths(columns.filter(({ hidden }) => !hidden))

  const [sortConfig, setSortConfig] = useState<{ column: string; direction: "asc" | "desc" } | undefined>();

  const toggleSort = (column: string) => {
    setSortConfig((prevConfig) => {
      if (prevConfig?.column === column) {
        return { column, direction: prevConfig.direction === "asc" ? "desc" : "asc" };
      }
      return { column, direction: "asc" }
    });
    setCurrentPage(1);
  }

  const filteredRows = rows.filter((row) =>
    Object.values(row).some(
      (value) => {
        if (React.isValidElement(value)) {
          // If ReactNode, skip search
          return false;
        }
        return (
          value &&
          value.toString().toLowerCase().includes(filterText.toLowerCase())
        );
      })
  );

  const totalPages = Math.ceil(filteredRows.length / pageSize);


  const sortedRows = [...filteredRows].sort((a, b) => {
    if (!sortConfig) return 0;

    const { column, direction } = sortConfig;

    let valA = a[column];
    let valB = b[column];

    if (!(typeof valA === "number" && typeof valB === "number")) {
      valA = valA?.toString().toLowerCase() || "";
      valB = valB?.toString().toLowerCase() || "";
    }

    if (valA < valB) return direction === "asc" ? -1 : 1;
    if (valA > valB) return direction === "asc" ? 1 : -1;
    return 0;
  })


  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= Math.ceil(filteredRows.length / pageSize)) {
      setCurrentPage(newPage);
    }
  };

  const toggleHiddeDetail = (index: number) => {
    if (!hiddenDetails.includes(index)) {
      setHiddenDetails([...hiddenDetails, index])
    } else {
      const tagsUpdated = hiddenDetails.filter((id) => id !== index)
      setHiddenDetails(tagsUpdated)
    }
  };

  const detailBody = (index: number, detail: any) => {
    return (
      <div className={`detail-row-section ${hiddenDetails.includes(index) ? 'hidden' : ''}`}>
        <div className="detail-row-title">
          <p>Descripción</p><button onClick={() => toggleHiddeDetail(index)}>{hiddenDetails.includes(index) ? 'mostrar' : 'ocultar'}</button>
        </div>
        {parse(detail)}
      </div>)
  }

  const avoidDoubleClickEvent = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const toggleRowSelection = (id: string) => {
    const newSelection = selectedRows.includes(id)
      ? selectedRows.filter((rowId) => rowId !== id)
      : [...selectedRows, id];

    setSelectedRows(newSelection);
    setAllRowsSelected(false)

    if (handleRowSelection) {
      handleRowSelection(newSelection);
    }
  };

  const toggleAllRowsSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSelection = event.target.checked ? filteredRows.map(row => row[columnId]) : [];

    setAllRowsSelected(event.target.checked)
    setSelectedRows(newSelection);

    if (handleRowSelection) {
      handleRowSelection(newSelection);
    }
  };

  const renderRows = () => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    const currentRows = sortedRows.slice(start, end);

    return currentRows.map((row, index) => (
      <React.Fragment key={index}>
        <tr className={`data-row ${selectedRows.includes(row[columnId]) ? 'selected' : ''}`}
          onDoubleClick={() => onRowDoubleClick(row)}
        >
          {(selectable) && (
            <td className="selectable-row" onDoubleClick={avoidDoubleClickEvent}>
              <input type="checkbox" checked={selectedRows.includes(row[columnId])} onChange={() => { toggleRowSelection(row[columnId]) }} />
            </td>
          )}
          {visibleColumns.map((col) => (
            <td key={col.key} style={{ width: col.width }}>
              {col.key + "_render" in row ?
                row[col.key + "_render"] : col.key === columnId ?
                  <span className="data-row-id-value">
                    <button onClick={() => onClickIdValue && onClickIdValue(row[columnId])}>
                      {hashtagPrefixId ? '#' : ''}{row[columnId]}
                    </button>
                  </span> : row[col.key]}
            </td>
          ))}
        </tr>
        {hasDetail && row.description && (
          <tr className='detail-row'>
            <td colSpan={visibleColumns.length + 1}>
              {detailBody(index, row[detailColumn])}
            </td>
          </tr>
        )}
      </React.Fragment>
    ));
  };


  return (
    <div className="table-container">
      <div className="filter-container">
        <input
          type="text"
          placeholder="Filtro rápido..."
          value={filterText}
          onChange={(e) => {
            setFilterText(e.target.value);
            setCurrentPage(1);
          }}
          className="filter-input"
        />
      </div>

      <table className="table-header">
        <colgroup>
          {(selectable) && (<col className='dummy-selectable-rows' />)}
          {visibleColumns.map((col) => (
            <col key={col.key} style={{ width: col.width }} />
          ))}
        </colgroup>

        <thead>
          <tr>
            {(selectable) && (
              <th className="selectable-allrows" style={{ width: '2%' }}>
                <input type="checkbox" checked={allRowsSelected} onChange={toggleAllRowsSelection} />
              </th>
            )}
            {visibleColumns.map((col) => (
              <th key={col.key} style={{ width: col.width }}>
                <div className="header-content">
                  <span>{col.name}</span>

                  <button className="table-sorting" onClick={() => toggleSort(col.key)}>
                    {sortConfig?.column === col.key
                      ? sortConfig.direction === "asc"
                        ? <ArrowUpIcon />
                        : <ArrowDownIcon />
                      : <UpDownArrowIcon />}
                  </button>

                </div>
              </th>
            ))}
          </tr>
        </thead>
      </table>

      <div className={`table-body-wrapper ${(!loading && isFading) ? "fade-out" : "fade-in"}`}>
        <table className="table-body">
          <colgroup>
            {(selectable) && (<col style={{ width: '2%' }} />)}
            {visibleColumns.map((col) => (
              <col key={col.key} style={{ width: col.width }} />
            ))}
          </colgroup>

          {(loading || isFading) ? (
            <tbody>
              <tr className="expanded-row">
                <td colSpan={visibleColumns.length + 1}>
                  <div className="table-loading-section">
                    < LoadingSpinner />
                  </div>
                </td>
              </tr></tbody>) : <tbody className="table-content">{renderRows()}</tbody>
          }

        </table>

        <div className="pagination">

          <div className="pagination_count">
            {`Mostando ${1 + (currentPage - 1) * pageSize}-${Math.min(currentPage * pageSize, filteredRows.length)} de ${filteredRows.length}`}
          </div>

          <div className="pagination_results">
            {(totalPages === 0 ? 'Sin Resultados' :
              `Página ${currentPage} de ${totalPages}`
            )}

          </div>

          <div className="pagination_btns">
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              Anterior
            </button>
            <button onClick={() => handlePageChange(currentPage + 1)}
              disabled={totalPages === 0 || currentPage === Math.ceil(filteredRows.length / pageSize)}>
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
